import React, { useEffect, useRef, useState } from 'react'
import testDatasetService, { TestDatasetProjectNode, TestDatasetSummary } from '../services/testDatasetService'
import labelService from '../services/labelService'

interface Props {
  userInfo: { email: string; fullName: string; isAdmin: boolean; permissions: string[] }
}

type NodeKind = 'project' | 'dataset'

type TreeNode = {
  id: number
  name: string
  kind: NodeKind
  parent_id?: number | null
  description?: string | null
  actions?: { view?: boolean; build?: boolean; download?: boolean; delete?: boolean; create?: boolean }
}

type LabelVersionNode = {
  id: number
  name: string
  path: string
  status?: string
  matched?: number
  is_test_locked?: boolean
}

export default function TestDatasetManagement({ userInfo }: Props) {
  const [nodes, setNodes] = useState<TreeNode[]>([])
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [selected, setSelected] = useState<TreeNode | null>(null)
  const [descExpanded, setDescExpanded] = useState(false)
  const [childCounts, setChildCounts] = useState<Record<number, number>>({})
  const [isBuildOpen, setBuildOpen] = useState(false)
  const [labelVersions, setLabelVersions] = useState<LabelVersionNode[]>([])
  const [labelVersionsLoading, setLabelVersionsLoading] = useState(false)
  const [selectedLabelVersionId, setSelectedLabelVersionId] = useState<number | null>(null)
  const [buildLoading, setBuildLoading] = useState(false)
  const [buildError, setBuildError] = useState<string | null>(null)
  const [activeJob, setActiveJob] = useState<{ jobId: number; status: string; step?: string; progress?: number } | null>(null)
  const [latest, setLatest] = useState<{ ready: boolean; expires_at?: string; download_url?: string } | null>(null)
  const [summary, setSummary] = useState<TestDatasetSummary | null>(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; node: TreeNode | null; typed: string; isJob?: boolean; jobId?: number }>({ open: false, node: null, typed: '' })
  const [isCreateOpen, setCreateOpen] = useState(false)
  const [createForm, setCreateForm] = useState({ projectName: '', datasetName: '', projectDescription: '', datasetDescription: '', existingProjectId: 0 })
  const [createError, setCreateError] = useState<string | null>(null)
  const [createLoading, setCreateLoading] = useState(false)
  const pollStopRef = useRef(false)
  const pollTimerRef = useRef<number | null>(null)

  useEffect(() => { loadProjects() }, [])

  useEffect(() => {
    if (isDownloading) document.body.style.cursor = 'wait'
    else document.body.style.cursor = ''
    return () => { document.body.style.cursor = '' }
  }, [isDownloading])

  // childCounts 계산
  useEffect(() => {
    if (!nodes || nodes.length === 0) return
    const counts: Record<number, number> = {}
    for (const n of nodes) {
      if (n.parent_id != null) counts[n.parent_id] = (counts[n.parent_id] || 0) + 1
    }
    setChildCounts(counts)
  }, [nodes])

  const loadProjects = async () => {
    try {
      const list = await testDatasetService.listProjects()
      setNodes(list as TreeNode[])
    } catch {
      setNodes([])
    }
  }

  useEffect(() => {
    if (!selected || selected.kind !== 'dataset') {
      setLatest(null); setSummary(null); setActiveJob(null)
      return
    }
    loadNodeStatus(selected.id)
  }, [selected])

  const loadNodeStatus = async (nodeId: number) => {
    try {
      const lat = await testDatasetService.getLatest(nodeId)
      setLatest(lat)
      if (lat.active_job) {
        // 새로고침 후 진행 중인 잡 복원
        const aj = lat.active_job
        setActiveJob({ jobId: aj.job_id, status: aj.status, step: aj.step, progress: aj.progress })
        startPolling(aj.job_id, nodeId)
        setSummary(null)
      } else if (lat.ready) {
        setActiveJob(null)
        try { setSummary(await testDatasetService.getSummary(nodeId)) } catch { setSummary(null) }
      } else {
        setSummary(null)
      }
    } catch { setLatest(null); setSummary(null) }
  }

  const loadLabelVersions = async () => {
    setLabelVersionsLoading(true)
    try {
      const roots = await labelService.getRootNodes()
      const versions: LabelVersionNode[] = []
      const traverse = async (nodeId: number, parentPath: string) => {
        const children = await labelService.getNodeChildren(nodeId)
        for (const child of children) {
          const namePath = parentPath ? `${parentPath} / ${child.name}` : child.name
          if (child.depth === 2) {
            try {
              const status = await labelService.getLabelSetStatus(child.id)
              versions.push({ id: child.id, name: child.name, path: namePath, status: status?.status, matched: status?.matched, is_test_locked: (status as any)?.is_test_locked })
            } catch { versions.push({ id: child.id, name: child.name, path: namePath }) }
          } else { await traverse(child.id, namePath) }
        }
      }
      for (const root of roots) await traverse(root.id, root.name)
      // 실제 라벨셋이 업로드된 항목만 표시 (status='none'은 업로드 전 노드)
      setLabelVersions(versions.filter(v => v.status && v.status !== 'none'))
    } catch { setLabelVersions([]) }
    finally { setLabelVersionsLoading(false) }
  }

  const startPolling = (jobId: number, nodeId: number) => {
    pollStopRef.current = false
    const poll = async () => {
      if (pollStopRef.current) return
      try {
        const s = await testDatasetService.getJobStatus(jobId)
        setActiveJob({ jobId, status: s.status, step: s.step, progress: s.progress })
        if (s.status === 'completed' || s.status === 'failed') {
          pollStopRef.current = true
          if (s.status === 'completed') await loadNodeStatus(nodeId)
          return
        }
      } catch {}
      pollTimerRef.current = window.setTimeout(poll, 3000)
    }
    poll()
  }

  const stopPolling = () => {
    pollStopRef.current = true
    if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null }
  }

  const projects = nodes.filter(n => n.kind === 'project').sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
  const childrenOf = (pid: number) => nodes.filter(n => n.kind === 'dataset' && n.parent_id === pid).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
  const isDatasetLeaf = (node: TreeNode | null) => node?.kind === 'dataset'

  const handleBuild = async () => {
    if (!selected || selected.kind !== 'dataset' || !selectedLabelVersionId) return
    setBuildLoading(true); setBuildError(null)
    try {
      stopPolling(); setActiveJob(null)
      const res = await testDatasetService.buildTestDataset(selected.id, selectedLabelVersionId)
      if (res.job_id) {
        setActiveJob({ jobId: res.job_id, status: 'queued' })
        setBuildOpen(false)
        startPolling(res.job_id, selected.id)
      }
    } catch (e: any) { setBuildError(e.message || '빌드 요청 실패') }
    finally { setBuildLoading(false) }
  }

  const handleDownload = async () => {
    if (!selected) return
    setIsDownloading(true)
    try {
      const input = window.prompt('다운로드할 파일명을 입력하세요 (확장자 없이)', `test_dataset_${selected.name}`)
      if (input === null) return
      const cleaned = (input || '').trim().replace(/[\\/\0]/g, '')
      if (!cleaned) { alert('유효한 파일명을 입력하세요.'); return }
      await testDatasetService.download(selected.id, cleaned)
    } catch (e: any) { alert(e.message || '다운로드 실패') }
    finally { window.setTimeout(() => setIsDownloading(false), 1200) }
  }

  const handleDeleteConfirm = async () => {
    if (!confirmDelete.node) return
    setIsDeleting(true)
    try {
      if (confirmDelete.isJob && confirmDelete.jobId) {
        await testDatasetService.deleteJob(confirmDelete.jobId)
        setActiveJob(null); setLatest(null); setSummary(null)
        alert('평가 데이터셋이 삭제되었습니다.\n라벨셋 잠금이 해제되어 학습 데이터셋으로 사용 가능합니다.')
      } else if (confirmDelete.node.kind === 'dataset') {
        await testDatasetService.deleteNode(confirmDelete.node.id)
        if (selected?.id === confirmDelete.node.id) setSelected(null)
      } else {
        await testDatasetService.deleteProject(confirmDelete.node.id)
        if (selected?.id === confirmDelete.node.id || selected?.parent_id === confirmDelete.node.id) setSelected(null)
      }
      await loadProjects()
    } catch (e: any) { alert(e.message || '삭제 실패') }
    finally { setIsDeleting(false); setConfirmDelete({ open: false, node: null, typed: '' }) }
  }

  const handleCreateSubmit = async () => {
    if (!createForm.projectName.trim() && !createForm.existingProjectId) {
      setCreateError('프로젝트명을 입력해주세요.'); return
    }
    setCreateLoading(true); setCreateError(null)
    try {
      const res = await testDatasetService.createProject({
        projectName: createForm.projectName.trim(),
        projectDescription: createForm.projectDescription || undefined,
        datasetName: createForm.datasetName || undefined,
        datasetDescription: createForm.datasetDescription || undefined,
        existingProjectId: createForm.existingProjectId || undefined,
      })
      setCreateOpen(false)
      setCreateForm({ projectName: '', datasetName: '', projectDescription: '', datasetDescription: '', existingProjectId: 0 })
      await loadProjects()
      if (res.dataset_id) {
        setExpanded(prev => { const ns = new Set(prev); ns.add(res.project_id); return ns })
      }
    } catch (e: any) { setCreateError(e.message || '생성 실패') }
    finally { setCreateLoading(false) }
  }

  const stepLabel: Record<string, string> = {
    init: '초기화 중', collect: '파일 수집 중', lock: '라벨셋 잠금 중', packaging: '압축 파일 생성 중', complete: '완료',
  }

  const canCreate = userInfo.isAdmin

  return (
    <div className="h-full flex flex-col">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">평가 데이터셋 관리</h1>
        <p className="text-gray-600">라벨 데이터를 선택하여 평가 데이터셋을 생성하고 다운로드합니다.</p>
      </div>

      <div className="flex-1 flex border border-gray-200 rounded-lg overflow-hidden bg-white">
        {/* ── 좌측 트리 패널 ── */}
        <div className="w-80 border-r border-gray-200 bg-gray-50">
          <div className="p-4 min-h-16 border-b border-gray-200 bg-white flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">평가 데이터셋 구조</h3>
            {canCreate && (
              <button
                onClick={() => { setCreateForm({ projectName: '', datasetName: '', projectDescription: '', datasetDescription: '', existingProjectId: 0 }); setCreateOpen(true); setCreateError(null) }}
                className="px-3 py-1 bg-gray-800 text-white text-xs rounded hover:bg-gray-900"
              >
                + 프로젝트 생성
              </button>
            )}
          </div>
          <div className="p-2 overflow-y-auto" style={{ height: 'calc(100vh - 300px)' }}>
            {projects.length === 0 ? (
              <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 350px)' }}>
                <div className="text-center text-gray-500">
                  <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <p className="text-sm mb-2">등록된 평가 데이터셋 프로젝트가 없습니다</p>
                  {canCreate && (
                    <button onClick={() => { setCreateForm({ projectName: '', datasetName: '', projectDescription: '', datasetDescription: '', existingProjectId: 0 }); setCreateOpen(true); setCreateError(null) }}
                      className="px-3 py-1 bg-gray-800 text-white text-xs rounded hover:bg-gray-900">
                      프로젝트 생성
                    </button>
                  )}
                </div>
              </div>
            ) : (
              projects.map(proj => {
                const children = childrenOf(proj.id)
                const isExpanded = expanded.has(proj.id)
                return (
                  <div key={`project-${proj.id}`}>
                    <div
                      className={`flex items-center py-2 px-2 cursor-pointer rounded transition-colors ${selected?.id === proj.id && selected?.kind === 'project' ? 'bg-gray-100 border-l-4 border-gray-800 text-gray-900' : 'hover:bg-gray-100'}`}
                      style={{ paddingLeft: '12px' }}
                      onClick={() => { setSelected(proj); setDescExpanded(false); if (!expanded.has(proj.id)) setExpanded(prev => { const ns = new Set(prev); ns.add(proj.id); return ns }) }}
                    >
                      <svg className="w-5 h-5 text-gray-700 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
                      </svg>
                      {children.length > 0 && (
                        <button onClick={(e) => { e.stopPropagation(); const ns = new Set(expanded); ns.has(proj.id) ? ns.delete(proj.id) : ns.add(proj.id); setExpanded(ns) }}
                          className="mr-1 w-4 h-4 flex-shrink-0 flex items-center justify-center">
                          <svg className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      )}
                      <span className="text-sm font-medium text-gray-900 min-w-0 break-all">
                        {proj.name}{children.length > 0 ? ` (${childCounts[proj.id] ?? children.length})` : ''}
                      </span>
                    </div>
                    {isExpanded && children.map(node => (
                      <div
                        key={`dataset-${node.id}`}
                        className={`flex items-center py-2 px-2 cursor-pointer rounded transition-colors ${selected?.id === node.id && selected?.kind === 'dataset' ? 'bg-gray-100 border-l-4 border-gray-800 text-gray-900' : 'hover:bg-gray-100'}`}
                        style={{ paddingLeft: '36px' }}
                        onClick={() => { setSelected(node); setDescExpanded(false) }}
                      >
                        <svg className="w-5 h-5 text-gray-700 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
                        </svg>
                        <span className="text-sm font-medium text-gray-900 min-w-0 break-all">{node.name}</span>
                      </div>
                    ))}
                  </div>
                )
              })
            )}
          </div>
        </div>

        {/* ── 우측 상세 패널 ── */}
        <div className="flex-1 bg-white">
          <div className="p-4 min-h-16 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">{selected ? selected.name : '노드를 선택하세요'}</h3>
            </div>
            <div className="flex space-x-2">
              {isDatasetLeaf(selected) && (
                <>
                  {(!activeJob || activeJob.status === 'failed' || activeJob.status === 'completed') && !latest?.ready && (
                    <button
                      className="px-3 py-1 bg-gray-800 text-white text-sm rounded hover:bg-gray-900"
                      onClick={async () => { setSelectedLabelVersionId(null); setBuildError(null); await loadLabelVersions(); setBuildOpen(true) }}
                    >
                      평가 데이터셋 생성
                    </button>
                  )}
                  {latest?.ready && summary && (
                    <>
                      <button
                        className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        disabled={isDownloading}
                        onClick={handleDownload}
                      >
                        {isDownloading ? (
                          <span className="inline-flex items-center">
                            <svg className="animate-spin -ml-0.5 mr-2 h-4 w-4 text-gray-700" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                            </svg>
                          </span>
                        ) : '다운로드'}
                      </button>
                    </>
                  )}
                </>
              )}
              {selected?.kind === 'project' && canCreate && (
                <>
                  <button
                    className="px-3 py-1 text-sm bg-gray-800 text-white rounded hover:bg-gray-900"
                    onClick={() => { setCreateForm(f => ({ ...f, projectName: '', datasetName: '', existingProjectId: selected.id })); setCreateOpen(true); setCreateError(null) }}
                  >
                    + 평가 데이터셋 추가
                  </button>
                  <button
                    className="px-3 py-1 text-sm border border-red-300 text-red-700 rounded bg-white hover:bg-red-50"
                    onClick={() => setConfirmDelete({ open: true, node: selected!, typed: '' })}
                  >
                    프로젝트 삭제
                  </button>
                </>
              )}
              {selected?.kind === 'dataset' && canCreate && (
                <button
                  className="px-3 py-1 text-sm border border-red-300 text-red-700 rounded bg-white hover:bg-red-50"
                  onClick={() => setConfirmDelete({ open: true, node: selected!, typed: '' })}
                >
                  평가 데이터셋 삭제
                </button>
              )}
            </div>
          </div>

          {/* 설명 섹션 */}
          {selected && (
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-start mb-1 gap-2">
                <div className="text-base font-semibold text-gray-900">설명</div>
                <button
                  type="button"
                  className="text-xs px-2 py-0.5 border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-100"
                  onClick={() => setDescExpanded(v => !v)}
                >
                  {descExpanded ? '접기' : '더보기'}
                </button>
              </div>
              <div className={`text-sm whitespace-pre-wrap break-all leading-relaxed transition-all ${descExpanded ? '' : 'max-h-24 overflow-hidden'}`}>
                {selected.description && String(selected.description).trim().length > 0
                  ? <span className="text-gray-700">{selected.description}</span>
                  : <span className="text-gray-400 italic">입력한 내용이 없습니다</span>}
              </div>
            </div>
          )}

          {/* 본문 */}
          {!selected && (
            <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 400px)' }}>
              <div className="text-center text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <p className="text-lg font-medium">노드를 선택하세요</p>
                <p className="text-sm">좌측의 평가 데이터셋 구조에서 항목을 클릭하여 내용을 확인하세요.</p>
              </div>
            </div>
          )}

          {selected?.kind === 'project' && (
            <div className="p-4">
              <div className="mb-2 text-sm text-gray-700">하위 평가 데이터셋</div>
              {(() => {
                const children = childrenOf(selected.id)
                if (children.length === 0) return <div className="text-sm text-gray-500">하위 데이터셋이 없습니다.</div>
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {children.map(ds => (
                      <div key={`ds-${ds.id}`} className="border rounded hover:bg-gray-50 cursor-pointer p-3"
                        onClick={() => { setSelected(ds); setDescExpanded(false) }}>
                        <div className="font-medium text-gray-900">{ds.name}</div>
                        <div className="text-xs text-gray-500 mt-1 overflow-hidden text-ellipsis whitespace-nowrap">
                          {ds.description?.trim() || '설명 없음'}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              })()}
            </div>
          )}

          {isDatasetLeaf(selected) && (
            <div className="p-4">
              {/* 요약 정보 */}
              {latest?.ready && summary ? (
                <div className="mt-2 border rounded p-6">
                  <div className="text-base font-semibold text-gray-900 mb-4">데이터셋 요약</div>
                  <div className="mb-6">
                    <div className="flex items-end gap-3">
                      <div className="text-3xl font-bold text-gray-900">{summary.total.toLocaleString()}</div>
                      <div className="text-xs text-gray-500 mb-1">총 이미지</div>
                    </div>
                  </div>
                  <div className="space-y-3 text-sm text-gray-700">
                    <div className="flex items-center gap-2">
                      <span>라벨:</span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-200 text-gray-800 text-xs">
                        {(summary.label_version_path || summary.label_version_name || '-') + (summary.label_archive_name ? ` > ${summary.label_archive_name}` : '')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>라벨 형식:</span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-200 text-gray-800 text-xs">{summary.label_format || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>작업 유형:</span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-200 text-gray-800 text-xs">{summary.label_task_type || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>크기:</span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-200 text-gray-800 text-xs">{formatBytes(summary.artifact_size)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>생성자:</span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-200 text-gray-800 text-xs">
                        {summary.created_by_name ? `${summary.created_by_name}${summary.created_by_email ? ` (${summary.created_by_email})` : ''}` : (summary.created_by_email || '-')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>생성일:</span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-green-100 text-green-800 text-xs">{formatLocalDate(summary.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>만료일:</span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-red-100 text-red-800 text-xs">{summary.expires_at ? formatLocalDate(summary.expires_at) : '알수없음'}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center" style={{ height: 'calc(100vh - 450px)' }}>
                  <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" strokeWidth="2" stroke="currentColor" viewBox="0 0 24 24">
                    <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20" />
                  </svg>
                  <p className="text-lg font-medium text-gray-900">아직 생성된 평가 데이터셋이 없습니다</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── 빌드 진행 모달 ── */}
      {activeJob && (activeJob.status === 'queued' || activeJob.status === 'running' || activeJob.status === 'failed') && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" role="dialog" aria-modal="true">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-3 p-6">
            {activeJob.status === 'failed' ? (
              <>
                <div className="text-base font-semibold text-gray-900 mb-3">빌드 실패</div>
                <div className="text-sm text-red-600 mb-6">빌드 중 오류가 발생했습니다. 다시 시도해 주세요.</div>
                <div className="flex justify-end">
                  <button
                    className="text-sm px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-900"
                    onClick={() => setActiveJob(null)}
                  >
                    닫기
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="text-base font-semibold text-gray-900 mb-1">평가 데이터셋 생성 중</div>
                <div className="text-xs text-gray-500 mb-5">{stepLabel[activeJob.step || ''] || '처리 중...'}</div>
                <div className="h-1.5 bg-gray-200 rounded-full mb-2">
                  <div className="h-full bg-gray-900 rounded-full transition-all duration-500" style={{ width: `${activeJob.progress ?? 0}%` }} />
                </div>
                <div className="text-right text-xs text-gray-500">{activeJob.progress ?? 0}%</div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── 빌드 모달 ── */}
      {isBuildOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" role="dialog" aria-modal="true">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg mx-3">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="text-base font-semibold text-gray-900">평가 데이터셋 생성</div>
              <p className="text-xs text-gray-500 mt-1">선택한 라벨셋은 생성 후 학습 데이터셋으로 사용할 수 없습니다.</p>
            </div>
            <div className="px-4 py-3">
              <div className="text-sm font-medium text-gray-700 mb-2">라벨 버전 선택</div>
              <div className="max-h-72 overflow-y-auto border border-gray-200 rounded">
                {labelVersionsLoading ? (
                  <div className="p-4 text-sm text-gray-400 text-center">로딩 중...</div>
                ) : labelVersions.length === 0 ? (
                  <div className="p-4 text-sm text-gray-400 text-center">사용 가능한 라벨 버전이 없습니다.</div>
                ) : labelVersions.map(v => {
                  const isLocked = v.is_test_locked
                  const isMatched = v.status === 'matched' || v.status === 'completed'
                  const isDisabled = isLocked || !isMatched
                  return (
                    <div
                      key={v.id}
                      onClick={() => { if (!isDisabled) setSelectedLabelVersionId(v.id) }}
                      className={`px-3 py-2 border-b border-gray-100 cursor-pointer ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'} ${selectedLabelVersionId === v.id ? 'bg-blue-50' : ''}`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-900">{v.name}</span>
                        <div className="flex gap-1">
                          {isLocked && (
                            <span className="text-xs px-2 py-0.5 rounded bg-red-50 text-red-600 border border-red-200">테스트 잠금</span>
                          )}
                          {!isLocked && (
                            <span className={`text-xs px-2 py-0.5 rounded border ${isMatched ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                              {v.status || '-'}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">{v.path}</div>
                      {v.matched != null && <div className="text-xs text-gray-500 mt-0.5">매칭: {v.matched.toLocaleString()}개</div>}
                    </div>
                  )
                })}
              </div>
              {buildError && (
                <div className="mt-3 p-2 bg-red-50 rounded text-sm text-red-700">{buildError}</div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
              <button className="text-sm px-3 py-1 border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-50"
                onClick={() => { setBuildOpen(false); setBuildError(null) }}>취소</button>
              <button
                className="text-sm px-3 py-1 rounded text-white bg-gray-800 hover:bg-gray-900 disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={!selectedLabelVersionId || buildLoading}
                onClick={handleBuild}
              >
                {buildLoading ? '요청 중...' : '생성 시작'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 생성 모달 ── */}
      {isCreateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" role="dialog" aria-modal="true">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-3">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="text-base font-semibold text-gray-900">
                {createForm.existingProjectId ? '평가 데이터셋 추가' : '새 프로젝트 생성'}
              </div>
            </div>
            <div className="px-4 py-3 space-y-3">
              {!createForm.existingProjectId && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">프로젝트명 *</label>
                    <input type="text" className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                      placeholder="프로젝트 이름을 입력하세요"
                      value={createForm.projectName} onChange={e => setCreateForm(f => ({ ...f, projectName: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">프로젝트 설명</label>
                    <input type="text" className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                      placeholder="선택사항"
                      value={createForm.projectDescription} onChange={e => setCreateForm(f => ({ ...f, projectDescription: e.target.value }))} />
                  </div>
                  <hr className="border-gray-200" />
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">평가 데이터셋명 *</label>
                <input type="text" className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                  placeholder="데이터셋 이름을 입력하세요"
                  value={createForm.datasetName} onChange={e => setCreateForm(f => ({ ...f, datasetName: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">데이터셋 설명</label>
                <input type="text" className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                  placeholder="선택사항"
                  value={createForm.datasetDescription} onChange={e => setCreateForm(f => ({ ...f, datasetDescription: e.target.value }))} />
              </div>
              {createError && (
                <div className="p-2 bg-red-50 rounded text-sm text-red-700">{createError}</div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
              <button className="text-sm px-3 py-1 border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-50"
                onClick={() => { setCreateOpen(false); setCreateForm({ projectName: '', datasetName: '', projectDescription: '', datasetDescription: '', existingProjectId: 0 }) }}>
                취소
              </button>
              <button className="text-sm px-3 py-1 rounded text-white bg-gray-800 hover:bg-gray-900 disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={createLoading} onClick={handleCreateSubmit}>
                {createLoading ? '생성 중...' : '생성'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 삭제 확인 모달 ── */}
      {confirmDelete.open && confirmDelete.node && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" role="dialog" aria-modal="true">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-3">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="text-base font-semibold text-gray-900">
                {confirmDelete.isJob ? '평가 데이터셋 삭제' : `${confirmDelete.node.kind === 'project' ? '프로젝트' : '평가 데이터셋'} 삭제`}
              </div>
            </div>
            <div className="px-4 py-3 space-y-3">
              {confirmDelete.isJob && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                  ⚠️ 평가 데이터셋을 삭제하면 라벨셋 잠금이 해제되어 학습 데이터셋으로 사용될 수 있습니다.
                </div>
              )}
              <p className="text-sm text-gray-700">
                삭제를 확인하려면 아래에 <strong>{confirmDelete.isJob ? '삭제' : confirmDelete.node.name}</strong>을 입력하세요.
              </p>
              <input
                autoFocus
                type="text"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                placeholder={confirmDelete.isJob ? '삭제' : confirmDelete.node.name}
                value={confirmDelete.typed}
                onChange={e => setConfirmDelete(d => ({ ...d, typed: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Escape') setConfirmDelete({ open: false, node: null, typed: '' }) }}
              />
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
              <button className="text-sm px-3 py-1 border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-50"
                onClick={() => setConfirmDelete({ open: false, node: null, typed: '' })}>취소</button>
              <button
                className="text-sm px-3 py-1 rounded text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={confirmDelete.typed !== (confirmDelete.isJob ? '삭제' : confirmDelete.node.name) || isDeleting}
                onClick={handleDeleteConfirm}
              >
                {isDeleting ? '삭제 중...' : '영구 삭제'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function formatBytes(n?: number | null): string {
  if (!n || n <= 0) return '-'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let idx = 0, val = n
  while (val >= 1024 && idx < units.length - 1) { val /= 1024; idx++ }
  return `${val.toFixed(1)} ${units[idx]}`
}

function formatLocalDate(value?: string): string {
  if (!value) return '-'
  try {
    const s = value.trim()
    const hasTZ = /[zZ]|([+-]\d{2}:?\d{2})$/.test(s)
    const normalized = hasTZ ? s : (s.includes('T') ? `${s}Z` : `${s.replace(' ', 'T')}Z`)
    const d = new Date(normalized)
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString()
  } catch { return value }
}
